import {
  FileType,
  ZyusetuFieldSectionFragmentFragment,
  ZyusetuFieldValueAnalyzedPayloadFragmentFragment,
} from "../../../graphql/graphql";
import {Accordion, AccordionDetails, AccordionSummary, Box, Stack, Tooltip, Typography,} from "@mui/material";
import {useCallback, useMemo, useRef, useState} from "react";
import {ZyusetuEditorSection} from "./ZyusetuEditorSection";
import {BadgeAlert, ChevronDown, Verified} from "lucide-react";
import {ZyusetuBrokerSelect} from "@/components/pages/Zyusetu/ZyusetuBrokerSelect.tsx";
import {Link} from "react-router-dom";
import dayjs from "dayjs";
import {ZyusetuEditType} from "@/types/ZyusetuEditType.ts";
import {ZyusetuConfirmSection} from "@/components/pages/Zyusetu/ZyusetuConfirmSection.tsx";
import {ZyusetuApprovalFlowSection} from "@/components/pages/Zyusetu/ZyusetuApprovalFlowSection.tsx";
import {useAtom} from "jotai";
import {ZyusetuAtom} from "@/plugin/jotai-wrapper.ts";
import {LoadingPage} from "@/pages/LoadingPage.tsx";

interface ZyusetuEditorProps {
  onFieldFocus: (
    value: ZyusetuFieldValueAnalyzedPayloadFragmentFragment,
  ) => void;
  
  editType: ZyusetuEditType
  approvalStepExecutionId?: string;
}

export function ZyusetuEditor({onFieldFocus, editType, approvalStepExecutionId}: ZyusetuEditorProps) {
  const onFocus = useCallback(
    (value?: ZyusetuFieldValueAnalyzedPayloadFragmentFragment | null) => {
      if (value) {
        onFieldFocus(value);
      }
    },
    [onFieldFocus],
  );
  
  const [zyusetu] = useAtom(ZyusetuAtom);
  
  const generatedFiles = useMemo(() => {
    return zyusetu?.files.filter(
      (zyusetu) => zyusetu.fileType === FileType.Zyusetu || zyusetu.fileType === FileType.Result,
    );
  }, [zyusetu?.files]);
  
  if (!zyusetu) {
    return <LoadingPage/>;
  }
  
  return (
    <>
      <Typography variant={"subtitle2"} my={2}>
        Tabキーで次のフォームを編集できます。
      </Typography>
      {[...zyusetu?.template?.sections ?? []]
        ?.sort((a, b) => a.index - b.index)
        ?.map(
          (section: ZyusetuFieldSectionFragmentFragment) => (
            <SectionEditorAccordion
              approvalStepExecutionId={approvalStepExecutionId}
              editType={editType}
              key={`section-${section.id}`}
              section={section}
              onFocus={onFocus}
              zyusetuId={zyusetu.id}
            />
          ),
        )}
      <ZyusetuBrokerSelect zyusetu={zyusetu}/>
      
      <ZyusetuConfirmSection zyusetu={zyusetu} editType={editType} approvalStepExecutionId={approvalStepExecutionId}/>
      
      <ZyusetuApprovalFlowSection zyusetu={zyusetu}/>
      
      {(generatedFiles?.length ?? 0) > 0 && (
        <Box my={3}>
          <Accordion elevation={0}>
            <AccordionSummary expandIcon={<ChevronDown/>} tabIndex={-1}>
              <Typography>これまで生成したアイテム</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                {generatedFiles
                  ?.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
                  ?.map((zyusetu) => (
                    zyusetu.url ?
                      <Typography
                        component={Link}
                        key={`file-history-${zyusetu.id}`}
                        to={zyusetu.url}
                      >
                        {dayjs(zyusetu.createdAt).format("YYYY/MM/DD HH:mm:ss")}{" "}
                        {zyusetu.title}
                      </Typography> : null
                  ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </>
  );
}

interface SectionEditorAccordionProps {
  section: ZyusetuFieldSectionFragmentFragment;
  onFocus: (
    value?: ZyusetuFieldValueAnalyzedPayloadFragmentFragment | null,
  ) => void;
  zyusetuId: string;
  editType: ZyusetuEditType
  approvalStepExecutionId?: string;
}

function SectionEditorAccordion({
                                  section,
                                  onFocus,
                                  zyusetuId,
                                  editType,
                                  approvalStepExecutionId
                                }: SectionEditorAccordionProps) {
  const isAllVerified = useMemo(() => {
    return section.keys.every((field) => field.value?.isVerified);
  }, [section.keys]);
  
  const hasUnreviewedChanges = useMemo(() => {
    return section.keys.some((field) => {
      return field.changedValues?.some(change => change.value !== field.value?.value);
    });
  }, [section.keys]);
  
  const [expanded, setExpanded] = useState(editType === ZyusetuEditType.Edit ? !isAllVerified : true);
  const accordionRef = useRef<HTMLDivElement>(null);
  
  const handleCloseAccordion = useCallback(() => {
    // 現在のアコーディオンの位置を取得
    const currentPosition = accordionRef.current?.getBoundingClientRect();
    
    // アコーディオンを閉じる
    setExpanded(false);
    
    // アコーディオンのSummaryが画面上部に見切れている場合のみスクロール
    if (currentPosition && currentPosition.top < 0) {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const accordionTop = currentPosition.top + scrollTop;
      
      // アニメーション完了後にスクロール
      setTimeout(() => {
        window.scrollTo({
          top: accordionTop - 16, // gridderの文のマージンを追加
          behavior: 'instant'
        });
      }, 300); // アニメーション完了を待つ
    }
  }, []);
  
  return (
    <Accordion
      ref={accordionRef}
       slotProps={{
        transition: {
          timeout: 300, // アコーディオンのアニメーションを無効化
        },
       }}
      expanded={expanded}
      onChange={() => setExpanded((prev) => !prev)}
    >
      <AccordionSummary
        tabIndex={-1}
        sx={{
          position: "sticky",
          top: 0,
          backgroundColor: "background.paper",
          zIndex: 1000,
          borderBottom: "1px solid",
          borderColor: "divider"
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
          }}
        >
          <Typography>{section.title}</Typography>
          
          <Box sx={{display: 'flex', gap: 1, ml: 1}}>
            {hasUnreviewedChanges ? (
              <Tooltip title="未確認の変更があります" arrow placement="top">
                <Box sx={{color: "warning.main"}}>
                  <BadgeAlert/>
                </Box>
              </Tooltip>
            ) : isAllVerified && (
              <Box
                sx={{
                  color: "primary.main",
                }}
              >
                <Verified/>
              </Box>
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <ZyusetuEditorSection
          approvalStepExecutionId={approvalStepExecutionId}
          editType={editType}
          zyusetuId={zyusetuId}
          section={section}
          onFocus={onFocus}
          onCloseAccordion={handleCloseAccordion}
        />
      </AccordionDetails>
    </Accordion>
  );
}

